import { FormEvent, useCallback, useState } from "react";
import SplitForm from "../../../components/SplitForm";
import SquareButton from "../../../components/Buttons/SquareButton";
import { useNavigate, useSearchParams } from "react-router-dom";
import AuthLabeledTextInput from "../../../components/Inputs/AuthLabeledTextInput";
import { snowTokenSupplier } from "../../../services/axios/webAuth";
import collectFormData from "../../../utils/form";
import errors from "../../../services/errors";
import axios, { isAxiosError } from "../../../services/axios/backendAxios";
import { User } from "../../../types";

export default function VerifyCodePage() {
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(false);
  const phoneNumber = searchParams.get("phoneNumber");
  const navigate = useNavigate();

  if (typeof phoneNumber !== "string" || phoneNumber.length < 3) {
    console.log("missing phone number - redirecting to phone entry screen");
    navigate("/login/v2");
  }

  const onFormSubmit = useCallback(
    async (e: FormEvent) => {
      setLoading(true);
      setErrorMessage(null);
      try {
        e.preventDefault();
        const form = collectFormData<{ code?: string }>(e.nativeEvent);
        if (typeof form.code === "string" && form.code.length === 6) {
          const authResult = await snowTokenSupplier.checkCode(
            phoneNumber!,
            Number(form.code),
          );
          if (authResult.ok) {
            // We are logged in :-)
            try {
              // Check if we have a user in the database.
              // eslint-disable-next-line @typescript-eslint/no-unused-vars
              const dbUser = (await axios.get("/user/me")).data.user as User;
              console.log("Login success!");
              navigate("/admin/");
            } catch (err) {
              if (isAxiosError(err) && err.response?.status === 404) {
                navigate("/sign-up"); // New user - get basic info
              } else {
                // unknown backend error...
                const errorId = await errors.report(err);
                setErrorMessage(
                  "Unable to lookup your user info. Please contact support. ErrorId: " +
                    errorId,
                );
              }
            }
          } else {
            // code validation failed:
            const error = authResult.error;
            setErrorMessage(error.message ?? `Invalid: ${error.code}`);
          }
        } else {
          errors.report(
            new Error("Unexpected form value for code: " + form.code),
          );
          setErrorMessage("Please make sure you entered a 6 digit code.");
        }
      } finally {
        setLoading(false);
      }
    },
    [navigate, phoneNumber],
  );

  return (
    <>
      <SplitForm leftContent={null}>
        <div className="grid h-full">
          <div className="text-full-white place-self-center">
            <form onSubmit={onFormSubmit} className="validate-code-form p-2">
              <h1 className="text-2xl lg:text-3xl font-bold">Login</h1>
              <p>
                We just sent a SMS message to {phoneNumber}. Please enter it
                below:
              </p>
              <AuthLabeledTextInput
                disabled={false}
                placeholder="Code"
                name="code"
                label="SMS Code"
                required
                minLength={6}
                maxLength={6}
                showValidation={false}
                autoFocus
              />
              {errorMessage ? (
                <div
                  className="text-snow-red p-2"
                  data-testid="sign-up-page-error-message"
                >
                  {errorMessage}
                </div>
              ) : null}
              <SquareButton
                label="Verify"
                className="max-w-none"
                testId="validate-code-submit"
                type="submit"
                onClick={undefined}
                disabled={loading}
                loading={loading}
                loaderColor={undefined}
                loaderSize={undefined}
              >
                Verify
              </SquareButton>
            </form>
          </div>
        </div>
      </SplitForm>
    </>
  );
}
